/**
 * WCAG 2.x contrast maths for hex colours.
 *
 * `parseHex` accepts #RGB and #RRGGBB (case-insensitive, hash optional) and
 * returns [r, g, b] in 0-255, or null for anything else -- callers treat null
 * as "unknown colour, use the fallback" rather than throwing, because skin
 * values come from the API and have been seen empty.
 */
export const parseHex = (value) => {
  if (typeof value !== 'string') return null;
  const hex = value.trim().replace(/^#/, '');
  if (!/^[0-9a-f]{3}$|^[0-9a-f]{6}$/i.test(hex)) return null;
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
};

const channel = (c) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

/** Relative luminance per WCAG 2.x, 0 (black) to 1 (white). Null for unparseable input. */
export const relativeLuminance = (value) => {
  const rgb = parseHex(value);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(channel);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Contrast ratio between two hex colours, 1 to 21. Null if either is
 * unparseable. WCAG thresholds: 3:1 for UI components and large text (1.4.11 /
 * 1.4.3 large), 4.5:1 for normal text.
 */
export const contrastRatio = (a, b) => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};
